import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import Moment from "moment";
import { apiHandler, getApiErrorMessage } from "../../../api/apihandler";
import { STUDENT_GAME_WEEKLY_REPORT, STUDENT_GAME_WEEKLY_REPORTS, TEACHERS_GET_ASSIGNED_GAMES_API } from "../../../api/constants";
import { getEffectiveStudentId } from "../../../utilities/teacherModulePermissions";
import { fetchStudentCompletedGames } from "../../../utilities/studentCompletedGames";
import LearningGameCard from "../../../components/common/LearningGameCard";
import "../../../assets/css/student-dashboard.css";

// Student-facing "Learning Progress" left-menu page: the Completed games
// section from the Student Dashboard plus a Weekly Reports button that opens
// the student's AI weekly game reports in a read-only modal, where they can
// step back through earlier weeks. (The teacher equivalent with the
// editable note + "Send to Parent" lives in learning-progress.js.)

// apiHandler() never throws - a failed request resolves with the raw axios
// error instead, which has no .data.
const isFailedResponse = (res) => !res || res?.isAxiosError || (res?.response && res?.data === undefined);

// week_start_date is a plain YYYY-MM-DD - parse it as a local date so it
// never shifts back a day in negative-offset timezones.
const formatWeek = (isoDate) => {
  const parsed = Moment(String(isoDate || "").slice(0, 10), "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : isoDate || "";
};

const SECTION_STYLES = {
  strengths: { title: "Strengths", icon: "star", border: "#9fe1cb", bg: "#e1f5ee", color: "#0f6e56" },
  priorities: { title: "Priorities", icon: "target", border: "#afa9ec", bg: "#eeedfe", color: "#4b3f9e" },
  activities: { title: "Suggested Activities", icon: "family_restroom", border: "#85b7eb", bg: "#e6f1fb", color: "#1d5a9e" },
};

function ReportSection({ type, items }) {
  const style = SECTION_STYLES[type];
  return (
    <div className="p-3 mb-2" style={{ border: `1px solid ${style.border}`, background: style.bg, borderRadius: "10px" }}>
      <div className="d-flex align-items-center mb-1" style={{ fontSize: "11px", fontWeight: 700, color: style.color, textTransform: "uppercase" }}>
        <i className="material-symbols-outlined me-1" style={{ fontSize: "16px" }}>{style.icon}</i>
        {style.title}
      </div>
      {Array.isArray(items) && items.length > 0 ? (
        <ul className="mb-0 ps-3" style={{ fontSize: "14px", color: "#2c2c2c" }}>
          {items.map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      ) : (
        <em className="text-muted small">None</em>
      )}
    </div>
  );
}

function SectionEmpty({ icon, text }) {
  return (
    <div className="dcl-section-empty">
      <div className="dcl-section-empty-icon">{icon}</div>
      <div className="dcl-section-empty-text">{text}</div>
    </div>
  );
}

function MyLearningProgress() {
  const navigate = useNavigate();
  const rawProfile = useSelector((state) => state.setting.setting.user_data);
  const profileData = useMemo(() => {
    if (!rawProfile) return {};
    if (typeof rawProfile === "object") return rawProfile;
    try {
      return JSON.parse(rawProfile);
    } catch (error) {
      return {};
    }
  }, [rawProfile]);
  const studentId = useMemo(() => getEffectiveStudentId(profileData), [profileData]);

  const [assignedGames, setAssignedGames] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(true);
  const [assignedError, setAssignedError] = useState(null);

  const [completedGames, setCompletedGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gamesError, setGamesError] = useState(null);

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const payload = studentId ? { student_id: studentId } : {};

    // Games the teacher has assigned to this student (all of them - no
    // attendance_date, unlike the Student Dashboard's per-day view). The
    // endpoint needs both student_id and user_id.
    (async () => {
      setAssignedLoading(true);
      setAssignedError(null);
      if (!studentId) {
        setAssignedGames([]);
        setAssignedLoading(false);
        return;
      }
      const res = await apiHandler(TEACHERS_GET_ASSIGNED_GAMES_API, "POST", {
        student_id: studentId,
        user_id: studentId,
      });
      if (cancelled) return;
      if (!isFailedResponse(res) && res?.data?.response?.status === 200) {
        const list = res?.data?.data?.assigned_games;
        setAssignedGames(Array.isArray(list) ? list : []);
      } else {
        setAssignedError(getApiErrorMessage(res, "Failed to load assigned learning games."));
        setAssignedGames([]);
      }
      setAssignedLoading(false);
    })();

    // student-games/completed-games isn't on the server - the shared helper
    // builds the list from every category's game progress instead.
    (async () => {
      setGamesLoading(true);
      setGamesError(null);
      const { games, failed } = await fetchStudentCompletedGames(studentId);
      if (cancelled) return;
      if (failed) {
        setGamesError("Failed to load completed learning games.");
        setCompletedGames([]);
      } else {
        setCompletedGames(games);
      }
      setGamesLoading(false);
    })();

    // Loaded up front so the button can show how many weeks exist and the
    // modal opens instantly.
    (async () => {
      setReportsLoading(true);
      setReportsError(null);
      const res = await apiHandler(STUDENT_GAME_WEEKLY_REPORTS, "POST", payload);
      if (cancelled) return;
      if (!isFailedResponse(res) && res?.data?.response?.status === 200) {
        const list = res?.data?.data?.reports;
        setReports(Array.isArray(list) ? list : []);
      } else {
        // The all-weeks route may not be on the server either - fall back
        // to the latest report (the one the Student Dashboard shows).
        const latest = await apiHandler(STUDENT_GAME_WEEKLY_REPORT, "POST", payload);
        if (cancelled) return;
        if (!isFailedResponse(latest) && latest?.data?.response?.status === 200) {
          const report = latest?.data?.data?.report;
          setReports(report ? [report] : []);
        } else {
          setReportsError(getApiErrorMessage(res, "Failed to load weekly reports."));
          setReports([]);
        }
      }
      setReportsLoading(false);
    })();

    return () => { cancelled = true; };
  }, [studentId]);

  // Same deep link the Student Dashboard's completed-game cards use.
  function openGame(game) {
    const gameNameId = game?.game_name_id || game?.game_name?.id;
    const categoryId = game?.game_name?.category?.id ?? game?.game_name?.category_id;
    if (!gameNameId || !categoryId) {
      navigate("/dashboard/app/game/game-category");
      return;
    }
    // AI-adaptive games live on the Adaptive Games tab.
    const mode = Number(game?.game_name?.is_ai_generated) === 1 ? "adaptive" : "static";
    navigate(`/dashboard/app/game/game-category?gameNameId=${gameNameId}&categoryId=${categoryId}&autoPlay=1&mode=${mode}`);
  }

  function openReportModal() {
    setSelectedIndex(0);
    setShowReportModal(true);
  }

  // Reports come newest first: a higher index is an earlier week.
  const selectedReport = reports[selectedIndex] || null;
  const hasPreviousWeek = selectedIndex < reports.length - 1;
  const hasNextWeek = selectedIndex > 0;

  return (
    <div id="content-page" className="content-page dcl-page">
      <style>{`
        .lp-student-header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .lp-student-header .lp-title-wrap { flex: 1; min-width: 200px; }
        .lp-weekly-btn {
          display: inline-flex; align-items: center; gap: 6px; border: none; border-radius: 10px;
          padding: 9px 16px; font-size: 14px; font-weight: 600; color: #fff;
          background: linear-gradient(135deg, #6f42c1, #8e5cd9);
          box-shadow: 0 4px 12px rgba(111, 66, 193, 0.25);
        }
        .lp-weekly-btn:hover:not(:disabled) { filter: brightness(1.05); }
        .lp-weekly-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .lp-weekly-btn .material-symbols-outlined { font-size: 18px; }
        .lp-week-nav { display: flex; align-items: center; gap: 8px; }
        .lp-week-nav .form-select { flex: 1; min-width: 0; }
        @media (max-width: 575.98px) {
          .lp-weekly-btn { width: 100%; justify-content: center; }
        }
      `}</style>
      <div className="dcl-wrap">
        <section className="dcl-section">
          <div className="dcl-section-body">
            <div className="lp-student-header">
              <div className="lp-title-wrap">
                <div className="d-flex align-items-center gap-2">
                  <div className="dcl-section-icon dcl-bg-blue">📈</div>
                  <div>
                    <div className="dcl-section-title">Learning Progress</div>
                    <div className="text-muted small">Your assigned and completed learning games and weekly learning reports</div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="lp-weekly-btn"
                onClick={openReportModal}
                disabled={reportsLoading}
              >
                {reportsLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="material-symbols-outlined">insights</i>
                )}
                Weekly Reports
                {!reportsLoading && reports.length > 0 ? (
                  <span className="badge rounded-pill bg-light text-dark ms-1">{reports.length}</span>
                ) : null}
              </button>
            </div>
          </div>
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-green">🎮</div>
            <div className="dcl-section-title">Assigned Learning Games</div>
            <span className="dcl-section-badge dcl-bg-green">{assignedGames.length} assigned</span>
          </div>
          <div className="dcl-section-body">
            {assignedLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" style={{ color: "#6f42c1" }} />
              </div>
            ) : assignedError ? (
              <SectionEmpty icon="⚠️" text={assignedError} />
            ) : assignedGames.length ? (
              <div className="dcl-games-grid">
                {assignedGames.map((game, idx) => (
                  <LearningGameCard
                    key={`assigned-${game?.id || idx}`}
                    game={game}
                    fallbackTitle={`Game ${idx + 1}`}
                    level={game?.level ?? game?.game_name?.level ?? null}
                    score={game?.score ?? null}
                    onPlay={openGame}
                  />
                ))}
              </div>
            ) : (
              <SectionEmpty icon="🎮" text="No learning games assigned yet" />
            )}
          </div>
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-blue">🏆</div>
            <div className="dcl-section-title">Completed Learning Games</div>
            <span className="dcl-section-badge dcl-bg-blue">{completedGames.length} completed</span>
          </div>
          <div className="dcl-section-body">
            {gamesLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" style={{ color: "#6f42c1" }} />
              </div>
            ) : gamesError ? (
              <SectionEmpty icon="⚠️" text={gamesError} />
            ) : completedGames.length ? (
              <div className="dcl-games-grid">
                {completedGames.map((game, idx) => (
                  <LearningGameCard
                    key={`completed-${game?.id || idx}`}
                    game={game}
                    completed
                    level={game?.level || game?.game_name?.level || 1}
                    score={game?.score ?? "--"}
                    onPlay={openGame}
                  />
                ))}
              </div>
            ) : (
              <SectionEmpty icon="🏆" text="No completed learning games yet" />
            )}
          </div>
        </section>
      </div>

      <Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Weekly Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportsError ? (
            <div className="text-center py-5">
              <i className="material-symbols-outlined" style={{ fontSize: "48px", color: "#dc3545" }}>error</i>
              <p className="text-danger mt-2 mb-0">{reportsError}</p>
            </div>
          ) : !selectedReport ? (
            <div className="text-center py-5 text-muted">
              No weekly report yet - keep playing the Learning Zone games and one will show up here.
            </div>
          ) : (
            <>
              {reports.length > 1 ? (
                <div className="lp-week-nav mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSelectedIndex(selectedIndex + 1)}
                    disabled={!hasPreviousWeek}
                    title="Previous week"
                  >
                    <i className="material-symbols-outlined" style={{ fontSize: "18px", verticalAlign: "middle" }}>chevron_left</i>
                  </Button>
                  <Form.Select value={selectedIndex} onChange={(e) => setSelectedIndex(Number(e.target.value))}>
                    {reports.map((report, index) => (
                      <option key={report.id || index} value={index}>
                        Week of {formatWeek(report.week_start_date)}{index === 0 ? " (latest)" : ""}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSelectedIndex(selectedIndex - 1)}
                    disabled={!hasNextWeek}
                    title="Next week"
                  >
                    <i className="material-symbols-outlined" style={{ fontSize: "18px", verticalAlign: "middle" }}>chevron_right</i>
                  </Button>
                </div>
              ) : null}

              <div style={{ border: "1px solid #eef0f3", borderRadius: "12px", overflow: "hidden" }}>
                <div className="px-3 py-2" style={{ background: "#f8f9fa" }}>
                  <div style={{ fontSize: "10px", color: "#aaa", textTransform: "uppercase" }}>Week of</div>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "#333" }}>{formatWeek(selectedReport.week_start_date)}</div>
                  {selectedReport.generated_at ? (
                    <div className="text-muted" style={{ fontSize: "12px" }}>
                      Generated {Moment(selectedReport.generated_at).format("MMM D, YYYY h:mm A")}
                    </div>
                  ) : null}
                </div>
                <div className="p-3">
                  {Array.isArray(selectedReport.weeklySummary) && selectedReport.weeklySummary.length > 0 ? (
                    <div className="table-responsive mb-3">
                      <table className="table table-sm table-bordered mb-0">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Sessions</th>
                            <th>Accuracy</th>
                            <th>Difficulty Reached</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedReport.weeklySummary.map((row, index) => (
                            <tr key={index}>
                              <td>{row.category}</td>
                              <td>{row.sessionsPlayed}</td>
                              <td>{row.accuracyPercent}%</td>
                              <td>{row.difficultyReached} / 5</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}

                  <ReportSection type="strengths" items={selectedReport.strengths} />
                  <ReportSection type="priorities" items={selectedReport.priorities} />
                  <ReportSection type="activities" items={selectedReport.activities} />

                  {selectedReport.parent_note ? (
                    <div className="p-3" style={{ border: "1px solid #f3c6da", background: "#fdf1f6", borderRadius: "10px" }}>
                      <div className="d-flex align-items-center mb-1" style={{ fontSize: "11px", fontWeight: 700, color: "#a3486b", textTransform: "uppercase" }}>
                        <i className="material-symbols-outlined me-1" style={{ fontSize: "16px" }}>mail</i>
                        Note to Parent
                      </div>
                      <div style={{ fontSize: "14px", color: "#2c2c2c", whiteSpace: "pre-line" }}>{selectedReport.parent_note}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MyLearningProgress;
